import { createFileRoute, useParams } from '@tanstack/react-router'
import { ContactServicesField } from '../../../../components/scenarios/ContactServicesField'
import { useUser, useDeleteUser, useUsers, useUpdateUser, useMergeUser, UserMutationError } from '../../../../api/queries'
import { Button } from '../../../../components/ui/button'
import { Badge } from '../../../../components/ui/badge'
import { Checkbox } from '../../../../components/ui/checkbox'
import { Link, useNavigate } from '@tanstack/react-router'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../../components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog'
import { mdiAccount, mdiAccountCog, mdiAccountKeyOutline, mdiAccountOffOutline, mdiEmailFastOutline, mdiAccountSwitchOutline, mdiAccountGroup, mdiArrowLeft, mdiArrowRight, mdiBaby, mdiCog, mdiLan, mdiPencil, mdiSourceMerge, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../../../../components/ui/icon'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { UserProperties } from '../../../../components/UserProperties'
import { EntityIssuesList } from '../../../../components/issues/EntityIssuesList'
import { EntityTagsSection } from '../../../../components/tags/EntityTagsSection'
import { NewConversationButton } from '../../../../components/conversations/NewConversationDialog'
import { EntityConversations } from '../../../../components/conversations/EntityConversations'
import { OverflowMenu, OverflowMenuItem, OverflowMenuSeparator } from '../../../../components/shared/OverflowMenu'
import { useAuth } from '../../../../contexts/AuthContext'
import { useCopyImpersonationLink } from '../../../../components/impersonation/useCopyImpersonationLink'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { UserContracts } from '../../../../components/users/user-contracts'
import { ContactPersonalData } from '../../../../components/data-protection/ContactPersonalData'
import { RetentionBadge } from '../../../../components/data-protection/RetentionBadge'
import { UserRelatedExpenses } from '../../../../components/users/user-related-expenses'
import { EstablishmentsSection, type EstablishmentUser } from '../../../../components/users/EstablishmentsSection'
import { PeopleSection, type PersonUser } from '../../../../components/users/PeopleSection'
import { EntityPicture } from '../../../../components/EntityPicture'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../../../api/client'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../../../../components/ApiSearchableSelect'
import { useLiveSubscription } from '../../../../contexts/LiveEventsContext'
import { useUserProperties, useSetUserPropertyValue, useUpdateUserPropertyValue, useDeleteUserPropertyValue } from '../../../../hooks/useQueries'
import { useCan } from '../../../../contexts/PermissionsContext'
import { ContactAccessGrants } from '../../../../components/users/ContactAccessGrants'
import { ContactSignInAccessDialog, useContactSignInAccess, useInviteContactToSignIn, useRevokeContactSignInAccess } from '../../../../components/users/ContactSignInAccess'
import { notify } from '../../../../lib/notifications'

const USER_TYPE_ICONS: Record<string, string> = {
  contact: mdiAccount,
  user: mdiAccountCog,
  user_group: mdiAccountGroup,
  company: SECTION_ICONS.organizations,
}

// Fetches the ancestor chain for a user by walking up parent_user_uid links
function useUserAncestors(orgCode: string, user: { parent_user_uid?: string | null } | undefined) {
  return useQuery({
    queryKey: ['userAncestors', orgCode, user?.parent_user_uid],
    queryFn: async () => {
      const ancestors: { uid: string; name: string }[] = []
      let currentParentUid = user?.parent_user_uid
      while (currentParentUid) {
        const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users/{userUid}', {
          params: { path: { orgCode, userUid: currentParentUid } }
        })
        if (!response.data) break
        ancestors.unshift({ uid: response.data.uid, name: response.data.name || '' })
        currentParentUid = response.data.parent_user_uid
      }
      return ancestors
    },
    enabled: !!user?.parent_user_uid,
  })
}

export const Route = createFileRoute('/$org/users/$userUid/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { org, userUid } = useParams({ from: '/$org/users/$userUid/' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('users')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Live entity notifications: the detail scope refreshes this user's own
  // row precisely; the collection scope is ALSO needed because the
  // children list and ancestor chain are OTHER users' rows, whose hints
  // carry those rows' own uids — only the collection scope's manual-key
  // prefixes (['users', org], ['userAncestors', org]) can catch them.
  useLiveSubscription({ entity: 'users', uid: userUid })
  useLiveSubscription({ entity: 'users' })

  // Use React Query hooks
  const {
    data: user,
    isLoading,
    error
  } = useUser(org, userUid)

  const { data: children = [] } = useUsers(org, { parent_uid: userUid })
  const { data: ancestors = [] } = useUserAncestors(org, user)

  // Establishments and People are both drawn from the same parent-scoped
  // children fetch above — the backend returns establishments unfiltered for
  // a parent_uid-scoped query (see internal/handlers/users/service.go
  // ListUsers), unlike the default type-agnostic list which excludes them.
  const establishments = useMemo(
    () => (children as EstablishmentUser[]).filter((c) => c.type === 'establishment'),
    [children]
  )
  const people = useMemo(
    () => (children as PersonUser[]).filter((c) => c.type === 'contact'),
    [children]
  )
  // The legacy flat "Child Contacts" list below would otherwise duplicate
  // every row already shown in Establishments/People for a company parent —
  // exclude those two child types there once the dedicated sections cover
  // them, keeping any other child type (e.g. a nested company) visible.
  const legacyChildren = useMemo(
    () => user?.type === 'company'
      ? children.filter((c: { type: string }) => c.type !== 'establishment' && c.type !== 'contact')
      : children,
    [children, user?.type]
  )

  const deleteUserMutation = useDeleteUser(org, {
    onSuccess: () => {
      navigate({ to: '/$org/users', params: { org }, search: {} })
    },
    onError: (error) => {
      console.error('Failed to delete user:', error)
    }
  })

  // --- Header action: Set parent (dialog with picker) ---
  const [showParentDialog, setShowParentDialog] = useState(false)
  const [parentDialogUid, setParentDialogUid] = useState<string>('')
  const [parentDialogError, setParentDialogError] = useState<string>('')

  // --- Header action: Merge into another user ---
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [mergeTargetUid, setMergeTargetUid] = useState<string>('')
  const [mergeTargetLabel, setMergeTargetLabel] = useState<string>('')
  const [mergeError, setMergeError] = useState<string>('')

  const mergeUserMutation = useMergeUser(org, {
    onSuccess: (target) => {
      setShowMergeDialog(false)
      navigate({ to: '/$org/users/$userUid', params: { org, userUid: target.uid } })
    },
    onError: (err) => {
      if (err instanceof UserMutationError) {
        if (err.errorCode === 'INVALID_PARENT_CYCLE') {
          setMergeError(t('detail.merge_cannot_be_descendant'))
        } else if (err.errorCode === 'INVALID_PARENT_SELF') {
          setMergeError(t('detail.merge_cannot_be_self'))
        } else {
          setMergeError(err.message || t('detail.merge_failed'))
        }
      } else {
        setMergeError(t('detail.merge_failed'))
      }
    },
  })

  // Inline-editing state for the user `code` field (click-to-edit, Enter to
  // save, Escape to cancel). Matches the inline-edit pattern used across the app.
  const USER_CODE_REGEX = /^[A-Z0-9_-]+$/
  const [isEditingCode, setIsEditingCode] = useState(false)
  const [editingCode, setEditingCode] = useState<string>('')
  const [codeError, setCodeError] = useState<string>('')

  const updateUserMutation = useUpdateUser(org, userUid)

  // Keep the local editing buffer in sync with the server value whenever it
  // changes (e.g. after a successful save) and we are not actively editing.
  useEffect(() => {
    if (!isEditingCode) {
      setEditingCode((user as { code?: string | null } | undefined)?.code ?? '')
    }
  }, [user, isEditingCode])

  const startEditingCode = () => {
    if (!canWriteContacts) return
    setCodeError('')
    setEditingCode(((user as { code?: string | null } | undefined)?.code) ?? '')
    setIsEditingCode(true)
  }

  const cancelEditingCode = () => {
    setCodeError('')
    setEditingCode(((user as { code?: string | null } | undefined)?.code) ?? '')
    setIsEditingCode(false)
  }

  const saveEditingCode = async () => {
    const trimmed = editingCode.trim()
    setCodeError('')

    if (trimmed !== '' && !USER_CODE_REGEX.test(trimmed)) {
      setCodeError(t('detail.code_invalid_format'))
      return
    }

    const currentCode = ((user as { code?: string | null } | undefined)?.code) ?? ''
    if (trimmed === currentCode) {
      setIsEditingCode(false)
      return
    }

    try {
      await updateUserMutation.mutateAsync({
        code: trimmed === '' ? null : trimmed,
      })
      setIsEditingCode(false)
    } catch (err) {
      if (err instanceof UserMutationError) {
        if (err.status === 409 || err.errorCode === 'duplicate_code') {
          setCodeError(t('detail.code_duplicate'))
        } else if (err.status === 400 || err.errorCode === 'invalid_code_format') {
          setCodeError(t('detail.code_invalid_format'))
        } else {
          setCodeError(err.message || t('detail.code_update_failed'))
        }
      } else {
        setCodeError(t('detail.code_update_failed'))
      }
    }
  }

  const handleCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void saveEditingCode()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditingCode()
    }
  }

  // --- Inline editing: Name ---
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingName, setEditingName] = useState<string>('')
  const [nameError, setNameError] = useState<string>('')

  useEffect(() => {
    if (!isEditingName) {
      setEditingName(user?.name ?? '')
    }
  }, [user, isEditingName])

  const startEditingName = () => {
    if (!canWriteContacts) return
    setNameError('')
    setEditingName(user?.name ?? '')
    setIsEditingName(true)
  }

  const cancelEditingName = () => {
    setNameError('')
    setEditingName(user?.name ?? '')
    setIsEditingName(false)
  }

  const saveEditingName = async () => {
    const trimmed = editingName.trim()
    setNameError('')
    if (trimmed === '') {
      setNameError(t('detail.name_required'))
      return
    }
    if (trimmed === (user?.name ?? '')) {
      setIsEditingName(false)
      return
    }
    try {
      await updateUserMutation.mutateAsync({ name: trimmed })
      setIsEditingName(false)
    } catch (err) {
      setNameError(err instanceof Error ? err.message : t('detail.name_update_failed'))
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void saveEditingName() }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEditingName() }
  }

  // --- Inline editing: Email ---
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [editingEmail, setEditingEmail] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')

  useEffect(() => {
    if (!isEditingEmail) {
      setEditingEmail(user?.email ?? '')
    }
  }, [user, isEditingEmail])

  const startEditingEmail = () => {
    setEmailError('')
    setEditingEmail(user?.email ?? '')
    setIsEditingEmail(true)
  }

  const cancelEditingEmail = () => {
    setEmailError('')
    setEditingEmail(user?.email ?? '')
    setIsEditingEmail(false)
  }

  const saveEditingEmail = async () => {
    const trimmed = editingEmail.trim()
    setEmailError('')
    if (trimmed !== '' && !EMAIL_REGEX.test(trimmed)) {
      setEmailError(t('detail.email_invalid_format'))
      return
    }
    if (trimmed === (user?.email ?? '')) {
      setIsEditingEmail(false)
      return
    }
    try {
      await updateUserMutation.mutateAsync({ email: trimmed })
      setIsEditingEmail(false)
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : t('detail.email_update_failed'))
    }
  }

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void saveEditingEmail() }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEditingEmail() }
  }

  // --- Inline editing: Phone ---
  // Unlike Name/Email/Type/Code (plain columns on the user row, written
  // through updateUserMutation), the phone number is the definition-backed
  // `phone` property (spec 2026-09-10-02) — it is read and written through
  // the property endpoints so validation applies and this row stays in sync
  // with the Properties section (which hides `phone` to avoid showing it
  // twice, see the `hiddenCodes` prop on <UserProperties> below).
  const { data: userPropertiesMap = {} } = useUserProperties(org, userUid)
  const setPhonePropertyMutation = useSetUserPropertyValue(org, userUid)
  const updatePhonePropertyMutation = useUpdateUserPropertyValue(org, userUid)
  const deletePhonePropertyMutation = useDeleteUserPropertyValue(org, userUid)
  // Parties (the /users surface) are the `contacts` resource, same gate
  // UserProperties.tsx uses for its own inline edits on this page.
  const canWriteContacts = useCan('contacts.write')
  // « Send a message » opens a thread: a conversations write, like every
  // other door of the ledger.
  const canWriteConversations = useCan('conversations.write')
  // « Copy impersonation link » (spec 2026-10-04-03): platform admins only,
  // on an account that isn't a platform admin nor the admin themselves (the
  // server refuses both anyway).
  const { user: me } = useAuth()
  // Sign-in access of a contact (spec 2026-10-06-06): a resident or a
  // provider signs in as this very row. Needs members.write on top of
  // contacts.write, like the backend.
  const canWriteMembers = useCan('members.write')
  const canManageSignIn = canWriteContacts && canWriteMembers && user?.type === 'contact'
  // Changing a contact's type touches how it signs in: members.write on top
  // of contacts.write, like the backend (spec 2026-10-20-00).
  const canEditType = canWriteContacts && canWriteMembers
  const { data: signInAccess } = useContactSignInAccess(org, userUid, canManageSignIn)
  const [showSignInDialog, setShowSignInDialog] = useState(false)
  const [showRevokeSignInDialog, setShowRevokeSignInDialog] = useState(false)
  const resendSignInLink = useInviteContactToSignIn(org, userUid)
  const revokeSignIn = useRevokeContactSignInAccess(org, userUid)
  const notifyLinkSent = useCallback((email?: string) => {
    notify({
      key: 'contact_sign_in_link',
      variant: 'default',
      titleKey: 'users:detail.sign_in_access.link_sent',
      values: { email: email ?? '' },
    })
  }, [])
  const impersonation = useCopyImpersonationLink()
  const canImpersonate = !!me?.is_platform_admin && user?.type === 'user'
    && !(user as { is_platform_admin?: boolean } | undefined)?.is_platform_admin && me?.uid !== userUid

  const currentPhone = typeof (userPropertiesMap as Record<string, unknown>).phone === 'string'
    ? (userPropertiesMap as Record<string, string>).phone
    : ''

  const [isEditingPhone, setIsEditingPhone] = useState(false)
  const [editingPhone, setEditingPhone] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string>('')

  useEffect(() => {
    if (!isEditingPhone) {
      setEditingPhone(currentPhone)
    }
  }, [currentPhone, isEditingPhone])

  const phoneMutationPending = setPhonePropertyMutation.isPending
    || updatePhonePropertyMutation.isPending
    || deletePhonePropertyMutation.isPending

  const startEditingPhone = () => {
    if (!canWriteContacts) return
    setPhoneError('')
    setEditingPhone(currentPhone)
    setIsEditingPhone(true)
  }

  const cancelEditingPhone = () => {
    setPhoneError('')
    setEditingPhone(currentPhone)
    setIsEditingPhone(false)
  }

  const saveEditingPhone = async () => {
    const trimmed = editingPhone.trim()
    setPhoneError('')
    if (trimmed === currentPhone) {
      setIsEditingPhone(false)
      return
    }
    try {
      const result = trimmed === ''
        ? await deletePhonePropertyMutation.mutateAsync('phone')
        : currentPhone === ''
          ? await setPhonePropertyMutation.mutateAsync({ propertyCode: 'phone', value: trimmed })
          : await updatePhonePropertyMutation.mutateAsync({ propertyCode: 'phone', value: trimmed })
      const maybeError = (result as { error?: { title?: string, message?: string } } | undefined)?.error
      if (maybeError) {
        throw new Error(maybeError.title || maybeError.message || t('detail.phone_update_failed'))
      }
      setIsEditingPhone(false)
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : t('detail.phone_update_failed'))
    }
  }

  // --- Postal convocation (spec 2026-10-05-08) ---
  // The co-owner asked for assembly convocations on paper: the
  // definition-backed `postal_convocation` boolean, read by convene. Written
  // through the property endpoints like the phone, hidden from the
  // Properties section so it shows once.
  const postalConvocation = (userPropertiesMap as Record<string, unknown>).postal_convocation === true
  const [postalError, setPostalError] = useState('')
  // The value just written, shown until the refetched properties carry it.
  const [postalWritten, setPostalWritten] = useState<boolean | null>(null)
  useEffect(() => {
    if (postalWritten !== null && postalWritten === postalConvocation) setPostalWritten(null)
  }, [postalWritten, postalConvocation])
  const togglePostalConvocation = async (checked: boolean) => {
    setPostalError('')
    setPostalWritten(checked)
    try {
      const res = await setPhonePropertyMutation.mutateAsync({ propertyCode: 'postal_convocation', value: checked })
      const maybeError = (res as { error?: { title?: string; message?: string } } | undefined)?.error
      if (maybeError) throw new Error(maybeError.title || maybeError.message || t('detail.postal_convocation_failed'))
    } catch (err) {
      setPostalWritten(null)
      setPostalError(err instanceof Error ? err.message : t('detail.postal_convocation_failed'))
    }
  }

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void saveEditingPhone() }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEditingPhone() }
  }

  // --- Inline editing: Parent user (searchable dropdown) ---
  const [isEditingParent, setIsEditingParent] = useState(false)
  const [editingParentUid, setEditingParentUid] = useState<string>('')
  const [parentError, setParentError] = useState<string>('')

  useEffect(() => {
    if (!isEditingParent) {
      setEditingParentUid(user?.parent_user_uid ?? '')
    }
  }, [user, isEditingParent])

  const childrenUids = useMemo(
    () => new Set((children as Array<{ uid: string }>).map(c => c.uid)),
    [children]
  )

  const searchUsers = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users', {
        params: {
          path: { orgCode: org },
          query: {
            ...(query ? { q: query, sort: 'name' } : { sort: '-updated_at' }),
            limit: 50,
          } as Record<string, string | number>,
        },
      })
      const users = ((response.data as { data?: Array<Record<string, unknown>> })?.data) || []
      return users
        .filter((u) => (u as { uid: string }).uid !== userUid)
        .filter((u) => !childrenUids.has((u as { uid: string }).uid))
        .map((u) => {
          const name = (u as { name?: string | null }).name
          const email = (u as { email?: string | null }).email
          const code = (u as { code?: string | null }).code
          const uid = (u as { uid: string }).uid
          const label = name
            ? (email ? `${name} · ${email}` : name)
            : (email || code || uid)
          return { value: uid, label }
        })
    } catch (err) {
      console.error('User search error:', err)
      return []
    }
  }, [org, userUid, childrenUids])

  const parentInitialOptions = useMemo(() => {
    const opts: ApiSearchableSelectOption[] = []
    if (user?.parent_user_uid) {
      opts.push({
        value: user.parent_user_uid,
        label: user.parent_user_name || user.parent_user_uid,
      })
    }
    return opts
  }, [user?.parent_user_uid, user?.parent_user_name])

  const startEditingParent = () => {
    if (!canWriteContacts) return
    setParentError('')
    setEditingParentUid(user?.parent_user_uid ?? '')
    setIsEditingParent(true)
  }

  const cancelEditingParent = () => {
    setParentError('')
    setEditingParentUid(user?.parent_user_uid ?? '')
    setIsEditingParent(false)
  }

  const saveEditingParent = async (uidOverride?: string | null) => {
    setParentError('')
    const target = uidOverride !== undefined ? uidOverride : editingParentUid
    const currentParent = user?.parent_user_uid ?? null
    const normalized: string | null = target && target !== '' ? target : null
    if (normalized === currentParent) {
      setIsEditingParent(false)
      return
    }
    try {
      await updateUserMutation.mutateAsync({ parent_user_uid: normalized })
      setIsEditingParent(false)
    } catch (err) {
      if (err instanceof UserMutationError) {
        if (err.errorCode === 'INVALID_PARENT_SELF') {
          setParentError(t('detail.parent_cannot_be_self'))
        } else if (err.errorCode === 'INVALID_PARENT_CYCLE') {
          setParentError(t('detail.parent_cannot_be_descendant'))
        } else {
          setParentError(err.message || t('detail.parent_update_failed'))
        }
      } else {
        setParentError(t('detail.parent_update_failed'))
      }
    }
  }

  // --- Inline editing: Type ---
  type UserType = 'contact' | 'user' | 'user_group' | 'company'
  const [isEditingType, setIsEditingType] = useState(false)
  const [editingType, setEditingType] = useState<UserType>('contact')
  const [typeError, setTypeError] = useState<string>('')

  useEffect(() => {
    if (!isEditingType && user?.type) {
      setEditingType(user.type as UserType)
    }
  }, [user, isEditingType])

  const startEditingType = () => {
    if (!canEditType) return
    setTypeError('')
    setEditingType((user?.type as UserType) ?? 'contact')
    setIsEditingType(true)
  }

  const cancelEditingType = () => {
    setTypeError('')
    setEditingType((user?.type as UserType) ?? 'contact')
    setIsEditingType(false)
  }

  const saveEditingType = async () => {
    setTypeError('')
    if (editingType === user?.type) {
      setIsEditingType(false)
      return
    }
    // Layer 1 guardrail: promoting to user requires a non-empty email.
    if (editingType === 'user' && !(user?.email ?? '').trim()) {
      setTypeError(t('detail.type_user_requires_email'))
      startEditingEmail()
      return
    }
    try {
      await updateUserMutation.mutateAsync({ type: editingType })
      setIsEditingType(false)
    } catch (err) {
      if (err instanceof UserMutationError) {
        const msg = (err.message || '').toLowerCase()
        if (msg.includes('email is required')) {
          setTypeError(t('detail.type_user_requires_email'))
          startEditingEmail()
        } else {
          setTypeError(err.message || t('detail.type_update_failed'))
        }
      } else {
        setTypeError(t('detail.type_update_failed'))
      }
    }
  }

  const handleTypeKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void saveEditingType()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditingType()
    }
  }

  // Set up breadcrumbs with full parent hierarchy
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.contacts'), path: `/${org}/users` },
    ...ancestors.map(a => ({ title: a.name, path: `/${org}/users/${a.uid}` })),
    { title: user?.name || t('types.contact') }
  ], [org, ancestors, user?.name, t])
  useBreadcrumbs(breadcrumbs)

  const getUserTypeBadge = (userType: string) => {
    const badges: Record<string, string> = {
      user: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      contact: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      user_group: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      company: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    }
    return badges[userType] || 'bg-muted text-muted-foreground'
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-muted animate-pulse rounded"></div>
          <div className="w-48 h-8 bg-muted animate-pulse rounded"></div>
        </div>
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="w-full h-64 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/$org/users"
            params={{ org }}
            search={{}}
            className="p-2 hover:bg-muted rounded-md"
          >
            <AppIcon path={mdiArrowLeft} size="md" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">{t('detail.not_found')}</h1>
        </div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">
            {(error as any)?.title || (error as any)?.message || 'User not found'}
          </div>
        </div>
      </div>
    )
  }

  const typeIcon = USER_TYPE_ICONS[user.type] || mdiAccount

  return (
    <div className="space-y-6">
      {/* Header. Same flow as PageHeader: below md the identity takes the
          whole row and the actions wrap under it, right-aligned, instead of
          pushing the page sideways. */}
      <div className="flex flex-wrap items-start gap-3 md:items-center">
        <div className="flex min-w-0 flex-1 basis-full items-center gap-4 md:basis-0">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {/* User Avatar */}
            <EntityPicture
              entityType="user"
              entityId={userUid}
              documentUid={user.picture?.document_uid}
              orgCode={org}
              size="128x128"
              editable={true}
              className="w-18 h-18 flex-shrink-0 rounded-full"
              alt={user.name || 'Contact'}
              onUpload={(success, error) => {
                if (success) {
                  queryClient.invalidateQueries({
                    queryKey: ['user', org, userUid]
                  });
                } else {
                  console.error('Failed to update profile picture:', error);
                }
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="min-w-0 text-2xl font-bold text-foreground [overflow-wrap:anywhere]" data-testid="user-name-heading">
                  {(user as { anonymized_at?: string | null }).anonymized_at ? t('data-protection:erased_person') : user.name}
                </h1>
                {(user as { code?: string | null }).code && (
                  <Badge
                    variant="outline"
                    className="text-xs font-mono text-muted-foreground"
                    data-testid="user-code-badge"
                    title={t('detail.code')}
                  >
                    {(user as { code?: string | null }).code}
                  </Badge>
                )}
                <EntityTagsSection org={org} entityType="user" entityUid={userUid} className="flex flex-wrap items-center gap-1" />
                <RetentionBadge
                  state={(user as { retention_state?: string | null }).retention_state}
                  until={(user as { retention_until?: string | null }).retention_until}
                  testId="user-retention-badge"
                />
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <span className={`inline-flex flex-shrink-0 items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadge(user.type)}`}>
                  <AppIcon path={typeIcon} size="xs" />
                  {t(`types.${user.type}` as any, { defaultValue: user.type })}
                </span>
                {user.email && (
                  <span className="min-w-0 truncate text-muted-foreground text-sm">{user.email}</span>
                )}
                {signInAccess?.active && (
                  <Badge variant="outline" className="flex-shrink-0 text-xs" data-testid="contact-sign-in-badge">
                    <AppIcon path={mdiAccountKeyOutline} size="xs" className="mr-1" />
                    {t(`detail.sign_in_access.badge.${signInAccess.kind ?? 'resident'}`)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          {/* One primary action, everything else behind « … » (patterns §33).
              Writing to this contact, from where the contact already is (spec
              2026-09-15-01 § B): the dialog is the queue's, pre-filled. */}
          {canWriteConversations && (
            <NewConversationButton
              org={org}
              contactUserUid={userUid}
              contactName={user.name ?? user.email ?? undefined}
              label={t('conversations:start.send_message')}
              icon={SECTION_ICONS.conversations}
              testId="contact-conversation-new"
            />
          )}
          <OverflowMenu testId="user-actions-menu" hidden={!canWriteContacts && user.type !== 'user' && !canImpersonate}>
            {canManageSignIn && !signInAccess?.active && (
              <OverflowMenuItem
                icon={mdiAccountKeyOutline}
                label={t('detail.sign_in_access.invite')}
                hint={user.email ? undefined : t('detail.sign_in_access.needs_email')}
                disabled={!user.email}
                onSelect={() => setShowSignInDialog(true)}
                testId="contact-sign-in-invite"
              />
            )}
            {canManageSignIn && signInAccess?.active && (
              <>
                <OverflowMenuItem
                  icon={mdiEmailFastOutline}
                  label={t('detail.sign_in_access.resend')}
                  disabled={resendSignInLink.isPending}
                  onSelect={() => {
                    resendSignInLink.mutate(
                      { kind: signInAccess.kind === 'provider' ? 'provider' : 'resident' },
                      { onSuccess: (access) => { if (access.link_sent) notifyLinkSent(access.email) } },
                    )
                  }}
                  testId="contact-sign-in-resend"
                />
                <OverflowMenuItem
                  icon={mdiAccountOffOutline}
                  label={t('detail.sign_in_access.revoke')}
                  onSelect={() => setShowRevokeSignInDialog(true)}
                  testId="contact-sign-in-revoke"
                />
              </>
            )}
            {canWriteContacts && (
              <OverflowMenuItem icon={mdiBaby} label={t('detail.create_child')} testId="create-child-contact-button">
                <Link to="/$org/users/create" params={{ org }} search={{ parent_user_uid: userUid }} />
              </OverflowMenuItem>
            )}
            {user.type === 'user' && (
              <OverflowMenuItem icon={mdiCog} label={t('detail.settings')} testId="settings-button">
                <Link to="/$org/users/$userUid/settings" params={{ org, userUid }} />
              </OverflowMenuItem>
            )}
            {canImpersonate && (
              <OverflowMenuItem
                icon={mdiAccountSwitchOutline}
                label={t('admin:impersonation.action')}
                disabled={impersonation.isPending}
                onSelect={() => { void impersonation.copy(userUid, org) }}
                testId="impersonate-button"
              />
            )}
            {canWriteContacts && (
              <>
                <OverflowMenuItem
                  icon={mdiLan}
                  label={t('detail.set_parent')}
                  onSelect={() => {
                    setParentDialogError('')
                    setParentDialogUid(user.parent_user_uid ?? '')
                    setShowParentDialog(true)
                  }}
                  testId="set-parent-button"
                />
                <OverflowMenuItem
                  icon={mdiSourceMerge}
                  label={t('detail.merge_into')}
                  onSelect={() => {
                    setMergeError('')
                    setMergeTargetUid('')
                    setMergeTargetLabel('')
                    setShowMergeDialog(true)
                  }}
                  testId="merge-into-button"
                />
                <OverflowMenuSeparator />
                <OverflowMenuItem
                  icon={mdiTrashCanOutline}
                  label={deleteUserMutation.isPending ? t('detail.deleting') : t('common:actions.delete')}
                  hint={children.length > 0 ? t('detail.delete_blocked_children') : undefined}
                  destructive
                  disabled={deleteUserMutation.isPending || children.length > 0}
                  onSelect={() => setShowDeleteDialog(true)}
                  testId="delete-button"
                />
              </>
            )}
          </OverflowMenu>
          {impersonation.dialog}
        </div>
      </div>

      {/* User Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('detail.basic_information')}</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="user-name-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.name')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingName ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={handleNameKeyDown}
                          className={`flex-1 px-3 py-2 border ${nameError ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                          disabled={updateUserMutation.isPending}
                          autoFocus
                          data-testid="user-name-input"
                          aria-invalid={Boolean(nameError)}
                        />
                        <Button
                          type="button"
                          onClick={() => { void saveEditingName() }}
                          disabled={updateUserMutation.isPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-name-save"
                        >
                          {updateUserMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingName}
                          disabled={updateUserMutation.isPending}
                          className="px-3 py-1 text-sm"
                          data-testid="user-name-cancel"
                        >
                          ✕
                        </Button>
                      </div>
                      {nameError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-name-error">
                          {nameError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span
                      role="button"
                      tabIndex={canWriteContacts ? 0 : -1}
                      className={`inline-block text-sm text-foreground px-2 py-1 rounded transition-colors ${canWriteContacts ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={startEditingName}
                      onKeyDown={(e) => {
                        if (canWriteContacts && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          startEditingName()
                        }
                      }}
                      title={t('detail.name')}
                      data-testid="user-name-value"
                    >
                      {user.name || (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
              <div data-testid="user-email-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.email')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingEmail ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={editingEmail}
                          onChange={(e) => setEditingEmail(e.target.value)}
                          onKeyDown={handleEmailKeyDown}
                          className={`flex-1 px-3 py-2 border ${emailError ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                          disabled={updateUserMutation.isPending}
                          autoFocus
                          data-testid="user-email-input"
                          aria-invalid={Boolean(emailError)}
                        />
                        <Button
                          type="button"
                          onClick={() => { void saveEditingEmail() }}
                          disabled={updateUserMutation.isPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-email-save"
                        >
                          {updateUserMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingEmail}
                          disabled={updateUserMutation.isPending}
                          className="px-3 py-1 text-sm"
                          data-testid="user-email-cancel"
                        >
                          ✕
                        </Button>
                      </div>
                      {emailError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-email-error">
                          {emailError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span
                      role="button"
                      tabIndex={canWriteContacts ? 0 : -1}
                      className={`inline-block text-sm text-foreground px-2 py-1 rounded transition-colors ${canWriteContacts ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={() => { if (canWriteContacts) startEditingEmail() }}
                      onKeyDown={(e) => {
                        if (canWriteContacts && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          startEditingEmail()
                        }
                      }}
                      title={t('detail.email')}
                      data-testid="user-email-value"
                    >
                      {user.email || (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
              <div data-testid="user-phone-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.phone')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingPhone ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="tel"
                          value={editingPhone}
                          onChange={(e) => setEditingPhone(e.target.value)}
                          onKeyDown={handlePhoneKeyDown}
                          className={`flex-1 px-3 py-2 border ${phoneError ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                          disabled={phoneMutationPending}
                          autoFocus
                          data-testid="user-phone-input"
                          aria-invalid={Boolean(phoneError)}
                        />
                        <Button
                          type="button"
                          onClick={() => { void saveEditingPhone() }}
                          disabled={phoneMutationPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-phone-save"
                        >
                          {phoneMutationPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingPhone}
                          disabled={phoneMutationPending}
                          className="px-3 py-1 text-sm"
                          data-testid={`cancel-phone-${userUid}`}
                        >
                          ✕
                        </Button>
                      </div>
                      {phoneError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-phone-error">
                          {phoneError}
                        </p>
                      )}
                    </div>
                  ) : currentPhone ? (
                    // A value is set: the number itself is a `tel:` link
                    // (clicking it dials, it must not also drop the user
                    // into edit mode), with a separate small edit trigger
                    // next to it — unlike Email/Parent/Code, the click-to-edit
                    // affordance can't live on the value text here.
                    <div className="flex items-center gap-1">
                      <a
                        href={`tel:${currentPhone}`}
                        className="inline-block text-sm text-foreground px-2 py-1 rounded hover:underline"
                        title={t('detail.phone')}
                        data-testid="user-phone-value"
                      >
                        {currentPhone}
                      </a>
                      {canWriteContacts && (
                        <button
                          type="button"
                          onClick={startEditingPhone}
                          className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
                          aria-label={t('detail.phone')}
                          title={t('detail.phone')}
                          data-testid="user-phone-edit"
                        >
                          <AppIcon path={mdiPencil} size="xs" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span
                      role="button"
                      tabIndex={canWriteContacts ? 0 : -1}
                      className={`inline-block text-sm text-muted-foreground italic px-2 py-1 rounded transition-colors ${canWriteContacts ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={startEditingPhone}
                      onKeyDown={(e) => {
                        if (canWriteContacts && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          startEditingPhone()
                        }
                      }}
                      title={t('detail.phone')}
                      data-testid="user-phone-value"
                    >
                      {t('detail.phone_empty_display')}
                    </span>
                  )}
                </dd>
              </div>
              <div data-testid="user-postal-convocation-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.postal_convocation')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  <label className="inline-flex items-center gap-2 px-2 py-1">
                    <Checkbox
                      checked={postalWritten ?? postalConvocation}
                      disabled={!canWriteContacts || setPhonePropertyMutation.isPending}
                      onCheckedChange={(checked) => { void togglePostalConvocation(checked) }}
                      data-testid="user-postal-convocation"
                    />
                    <span className="text-muted-foreground">{t('detail.postal_convocation_help')}</span>
                  </label>
                  {postalError && (
                    <p className="mt-1 text-sm text-destructive" data-testid="user-postal-convocation-error">{postalError}</p>
                  )}
                </dd>
              </div>
              <div data-testid="user-type-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.type')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingType ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <select
                          value={editingType}
                          onChange={(e) => setEditingType(e.target.value as UserType)}
                          onKeyDown={handleTypeKeyDown}
                          className={`flex-1 px-3 py-2 border ${typeError ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                          disabled={updateUserMutation.isPending}
                          autoFocus
                          data-testid="user-type-select"
                          aria-invalid={Boolean(typeError)}
                        >
                          <option value="contact">{t('types.contact')}</option>
                          <option value="user">{t('types.user')}</option>
                          <option value="user_group">{t('types.user_group')}</option>
                          <option value="company">{t('types.company')}</option>
                        </select>
                        <Button
                          type="button"
                          onClick={() => { void saveEditingType() }}
                          disabled={updateUserMutation.isPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-type-save"
                        >
                          {updateUserMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingType}
                          disabled={updateUserMutation.isPending}
                          className="px-3 py-1 text-sm"
                          data-testid="user-type-cancel"
                        >
                          ✕
                        </Button>
                      </div>
                      {typeError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-type-error">
                          {typeError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span
                      role="button"
                      tabIndex={canEditType ? 0 : -1}
                      className={`inline-flex items-center transition-opacity rounded-full ${canEditType ? 'cursor-pointer hover:opacity-80' : ''}`}
                      onClick={startEditingType}
                      onKeyDown={(e) => {
                        if (canEditType && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          startEditingType()
                        }
                      }}
                      title={t('detail.type')}
                      data-testid="user-type-value"
                    >
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadge(user.type)}`}>
                        <AppIcon path={typeIcon} size="xs" />
                        {t(`types.${user.type}` as any, { defaultValue: user.type })}
                      </span>
                    </span>
                  )}
                </dd>
              </div>
              <div data-testid="user-code-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.code')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingCode ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingCode}
                          onChange={(e) => setEditingCode(e.target.value)}
                          onKeyDown={handleCodeKeyDown}
                          className={`flex-1 px-3 py-2 border ${codeError ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                          disabled={updateUserMutation.isPending}
                          autoFocus
                          data-testid="user-code-input"
                          aria-invalid={Boolean(codeError)}
                        />
                        <Button
                          type="button"
                          onClick={() => { void saveEditingCode() }}
                          disabled={updateUserMutation.isPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-code-save"
                        >
                          {updateUserMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingCode}
                          disabled={updateUserMutation.isPending}
                          className="px-3 py-1 text-sm"
                          data-testid="user-code-cancel"
                        >
                          ✕
                        </Button>
                      </div>
                      {codeError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-code-error">
                          {codeError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span
                      role="button"
                      tabIndex={canWriteContacts ? 0 : -1}
                      className={`inline-block text-sm text-foreground px-2 py-1 rounded transition-colors ${canWriteContacts ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={startEditingCode}
                      onKeyDown={(e) => {
                        if (canWriteContacts && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          startEditingCode()
                        }
                      }}
                      title={t('detail.code')}
                      data-testid="user-code-value"
                    >
                      {(user as { code?: string | null }).code || (
                        <span className="text-muted-foreground italic">{t('detail.code_empty')}</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
              {/* The services the contact holds (spec 2026-11-03-02). */}
              <ContactServicesField orgCode={org} contactUid={userUid} />
              <div data-testid="user-parent-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.parent_contact')}</dt>
                <dd className="text-sm text-foreground mt-1">
                  {isEditingParent ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ApiSearchableSelect
                            value={editingParentUid}
                            placeholder={t('detail.parent_placeholder')}
                            searchPlaceholder={t('detail.parent_search_placeholder')}
                            emptyMessage={t('detail.parent_empty')}
                            onSearch={searchUsers}
                            initialOptions={parentInitialOptions}
                            onValueChange={(value) => setEditingParentUid(value)}
                            onOptionSelect={(opt) => {
                              setEditingParentUid(opt.value)
                              void saveEditingParent(opt.value)
                            }}
                            disabled={updateUserMutation.isPending}
                            minSearchLength={1}
                            debounceMs={150}
                            data-testid="user-parent-select"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { void saveEditingParent(null) }}
                          disabled={updateUserMutation.isPending || !user.parent_user_uid}
                          className="px-3 py-1 text-sm"
                          title={t('detail.parent_clear')}
                          data-testid="user-parent-clear"
                        >
                          ∅
                        </Button>
                        <Button
                          type="button"
                          onClick={() => { void saveEditingParent() }}
                          disabled={updateUserMutation.isPending}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                          data-testid="user-parent-save"
                        >
                          {updateUserMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditingParent}
                          disabled={updateUserMutation.isPending}
                          className="px-3 py-1 text-sm"
                          data-testid="user-parent-cancel"
                        >
                          ✕
                        </Button>
                      </div>
                      {parentError && (
                        <p className="mt-1 text-sm text-destructive" data-testid="user-parent-error">
                          {parentError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span
                        role="button"
                        tabIndex={canWriteContacts ? 0 : -1}
                        className={`inline-block text-sm text-foreground px-2 py-1 rounded transition-colors ${canWriteContacts ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                        onClick={startEditingParent}
                        onKeyDown={(e) => {
                          if (canWriteContacts && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault()
                            startEditingParent()
                          }
                        }}
                        title={t('detail.parent_contact')}
                        data-testid="user-parent-value"
                      >
                        {user.parent_user_uid ? (
                          user.parent_user_name || t('list.view_parent')
                        ) : (
                          <span className="text-muted-foreground italic">{t('detail.parent_empty_display')}</span>
                        )}
                      </span>
                      {user.parent_user_uid && (
                        <Link
                          to="/$org/users/$userUid"
                          params={{ org, userUid: user.parent_user_uid }}
                          className="p-1 rounded hover:bg-muted text-primary"
                          title={t('detail.parent_open')}
                          data-testid="user-parent-navigate"
                        >
                          <AppIcon path={mdiArrowRight} size="sm" />
                        </Link>
                      )}
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </div>

        </div>
      </div>

      {/* Properties (website, business activity, address…), titled with the
          generic "Properties" label (#1765): phone and postal convocation
          show on the identity card instead. */}
      <UserProperties org={org} userUid={userUid} hiddenCodes={['phone', 'postal_convocation']} />

      {/* Establishments (shops/branches/service lines) — company contacts only */}
      {user.type === 'company' && (
        <EstablishmentsSection org={org} companyUid={userUid} establishments={establishments} />
      )}

      {/* People (named contacts at this company) — company contacts only */}
      {user.type === 'company' && (
        <PeopleSection org={org} people={people} />
      )}

      {/* Contracts */}
      <UserContracts org={org} userUid={userUid} userType={user.type} />

      {/* Expenses billed by this contact, grouped by contract */}
      <UserRelatedExpenses org={org} userUid={userUid} />

      {/* Places this contact has access to, with each grant's origin (spec 2026-10-06-07) */}
      {user.type === 'contact' && signInAccess?.active && <ContactAccessGrants org={org} userUid={userUid} />}

      {/* Issues */}
      <section data-testid="user-issues-section" className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-3">{t('detail.issues')}</h2>
        <EntityIssuesList targetType="user" targetEntityUid={user.uid} />
      </section>

      {/* Child Contacts (any child type not already covered by Establishments/People above) */}
      {legacyChildren.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">{t('detail.child_contacts', { count: legacyChildren.length })}</h2>
          <div className="divide-y divide-border">
            {legacyChildren.map((child: any) => {
              const childTypeIcon = USER_TYPE_ICONS[child.type] || mdiAccount
              return (
                <div key={child.uid} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadge(child.type)}`}>
                      <AppIcon path={childTypeIcon} size="xs" />
                      {t(`types.${child.type}` as any, { defaultValue: child.type })}
                    </span>
                    <Link
                      to="/$org/users/$userUid"
                      params={{ org, userUid: child.uid }}
                      className="text-primary hover:text-primary/80 hover:underline text-sm"
                      data-testid={`child-user-link-${child.uid}`}
                    >
                      {child.name || '-'}
                    </Link>
                    {child.email && (
                      <span className="text-muted-foreground text-sm">{child.email}</span>
                    )}
                  </div>
                  <span className="text-muted-foreground text-xs">
                    {child.created_at ? new Date(child.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Latest conversations with this contact; the section gates itself on
          conversations.read. */}
      <EntityConversations org={org} contactUid={userUid} />

      {/* Personal data, retention and disclosures (spec 2026-10-03-02): last
          on the page and collapsed by default (#1766). */}
      {user.type === 'contact' && <ContactPersonalData org={org} userUid={userUid} />}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {user.parent_user_uid
                ? t('detail.delete_dialog_description_with_parent', { name: user.name, parent: user.parent_user_name || t('detail.parent_contact') })
                : t('detail.delete_dialog_description', { name: user.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-dialog">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUserMutation.mutate(userUid)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-dialog"
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set Parent Dialog */}
      <Dialog open={showParentDialog} onOpenChange={setShowParentDialog}>
        <DialogContent data-testid="set-parent-dialog">
          <DialogHeader>
            <DialogTitle>{t('detail.set_parent_dialog_title')}</DialogTitle>
            <DialogDescription>{t('detail.set_parent_dialog_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <ApiSearchableSelect
              value={parentDialogUid}
              placeholder={t('detail.parent_placeholder')}
              searchPlaceholder={t('detail.parent_search_placeholder')}
              emptyMessage={t('detail.parent_empty')}
              onSearch={searchUsers}
              initialOptions={parentInitialOptions}
              onValueChange={(value) => setParentDialogUid(value)}
              onOptionSelect={(opt) => {
                setParentDialogUid(opt.value)
              }}
              disabled={updateUserMutation.isPending}
              minSearchLength={1}
              debounceMs={150}
              data-testid="set-parent-select"
            />
            {parentDialogError && (
              <p className="text-sm text-destructive" data-testid="set-parent-error">{parentDialogError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { void (async () => {
                setParentDialogError('')
                try {
                  await updateUserMutation.mutateAsync({ parent_user_uid: null })
                  setShowParentDialog(false)
                } catch (err) {
                  setParentDialogError(err instanceof Error ? err.message : t('detail.parent_update_failed'))
                }
              })() }}
              disabled={updateUserMutation.isPending || !user.parent_user_uid}
              data-testid="set-parent-clear"
            >
              {t('detail.parent_clear')}
            </Button>
            <Button
              type="button"
              onClick={() => { void (async () => {
                setParentDialogError('')
                const normalized = parentDialogUid && parentDialogUid !== '' ? parentDialogUid : null
                try {
                  await updateUserMutation.mutateAsync({ parent_user_uid: normalized })
                  setShowParentDialog(false)
                } catch (err) {
                  if (err instanceof UserMutationError) {
                    if (err.errorCode === 'INVALID_PARENT_SELF') setParentDialogError(t('detail.parent_cannot_be_self'))
                    else if (err.errorCode === 'INVALID_PARENT_CYCLE') setParentDialogError(t('detail.parent_cannot_be_descendant'))
                    else setParentDialogError(err.message || t('detail.parent_update_failed'))
                  } else {
                    setParentDialogError(t('detail.parent_update_failed'))
                  }
                }
              })() }}
              disabled={updateUserMutation.isPending || !parentDialogUid}
              data-testid="set-parent-confirm"
            >
              {updateUserMutation.isPending ? t('detail.saving') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Into Dialog */}
      {canManageSignIn && (
        <ContactSignInAccessDialog
          org={org}
          userUid={userUid}
          open={showSignInDialog}
          onOpenChange={setShowSignInDialog}
          onInvited={(access) => { if (access.link_sent) notifyLinkSent(access.email) }}
        />
      )}
      <AlertDialog open={showRevokeSignInDialog} onOpenChange={setShowRevokeSignInDialog}>
        <AlertDialogContent data-testid="contact-sign-in-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.sign_in_access.revoke_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.sign_in_access.revoke_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contact-sign-in-revoke-cancel">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="contact-sign-in-revoke-confirm"
              onClick={() => revokeSignIn.mutate()}
            >
              {t('detail.sign_in_access.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <AlertDialogContent data-testid="merge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.merge_dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {mergeTargetUid
                ? t('detail.merge_dialog_description', { source: user.name, target: mergeTargetLabel })
                : t('detail.merge_dialog_pick')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <ApiSearchableSelect
              value={mergeTargetUid}
              placeholder={t('detail.merge_placeholder')}
              searchPlaceholder={t('detail.parent_search_placeholder')}
              emptyMessage={t('detail.parent_empty')}
              onSearch={searchUsers}
              onValueChange={(value) => setMergeTargetUid(value)}
              onOptionSelect={(opt) => {
                setMergeTargetUid(opt.value)
                setMergeTargetLabel(opt.label)
              }}
              disabled={mergeUserMutation.isPending}
              minSearchLength={1}
              debounceMs={150}
              data-testid="merge-target-select"
            />
            {mergeError && (
              <p className="text-sm text-destructive" data-testid="merge-error">{mergeError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-merge-dialog">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog open on validation error; only mutate.
                e.preventDefault()
                setMergeError('')
                if (!mergeTargetUid) {
                  setMergeError(t('detail.merge_dialog_pick'))
                  return
                }
                mergeUserMutation.mutate({ sourceUid: userUid, targetUid: mergeTargetUid })
              }}
              disabled={mergeUserMutation.isPending || !mergeTargetUid}
              data-testid="confirm-merge-dialog"
            >
              {mergeUserMutation.isPending ? t('detail.merging') : t('detail.merge_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
